import {
  BatteryInputConnection,
  ProgramWizardCurrentProgram,
  ProgramWizardData,
  ProgramWizardOperatingMode,
  ProgramWizardStateData,
  ProgramWizardStep,
  ProgramWizardStepValue,
} from './ProgramWizard.types';

type TranslateFn = (key: string) => string;

// The standby/shutdown transition waits at most this long for AlfenSystemMode to report Standby
// before moving on to the fixed buffer below.
export const TRANSITION_PHASE1_MAX_WAIT_MS = 30_000;
// Extra buffer held after phase 1 ends, regardless of whether it ended early (Standby reported)
// or by timeout.
export const TRANSITION_EXTRA_WAIT_MS = 60_000;
export const SHUTDOWN_FINAL_WAIT_MS = 62_000;

export const isTransitionPhase1Complete = ({
  startedAt,
  modeIsStandby,
  now = Date.now(),
}: {
  startedAt: number;
  modeIsStandby: boolean;
  now?: number;
}): boolean => modeIsStandby || now - startedAt >= TRANSITION_PHASE1_MAX_WAIT_MS;

export const isTransitionWaitComplete = ({
  phase1CompletedAt,
  now = Date.now(),
}: {
  phase1CompletedAt: number | null;
  now?: number;
}): boolean => phase1CompletedAt !== null && now - phase1CompletedAt >= TRANSITION_EXTRA_WAIT_MS;

export const isShutdownFinalWaitComplete = ({
  shutdownCompletedAt,
  now = Date.now(),
}: {
  shutdownCompletedAt: number | null;
  now?: number;
}): boolean => shutdownCompletedAt !== null && now - shutdownCompletedAt >= SHUTDOWN_FINAL_WAIT_MS;

// Mirrors the standby/shutdown end conditions from the ticket: standby only needs the input
// disconnected once the wait is over; shutdown additionally needs the ESI stopped, the batteries
// off, and the shutdown window to complete.
export const isTransitionComplete = ({
  operatingMode,
  stateData,
  waitComplete,
  shutdownCompletedAt = null,
  now = Date.now(),
}: {
  operatingMode: ProgramWizardOperatingMode;
  stateData: ProgramWizardStateData;
  waitComplete: boolean;
  shutdownCompletedAt?: number | null;
  now?: number;
}): boolean => {
  if (operatingMode === ProgramWizardOperatingMode.STANDBY) {
    return waitComplete && !stateData.inputConnected;
  }
  if (operatingMode === ProgramWizardOperatingMode.SHUTDOWN) {
    return (
      waitComplete &&
      !stateData.inputConnected &&
      !stateData.converterStarted &&
      stateData.systemOff &&
      isShutdownFinalWaitComplete({ shutdownCompletedAt, now })
    );
  }
  return false;
};

export const IDLE_PROGRAM_DESCRIPTION = 'Idle';

const isProgramActive = (currentProgram: ProgramWizardCurrentProgram | null | undefined): boolean =>
  !!currentProgram && currentProgram.description !== IDLE_PROGRAM_DESCRIPTION;

const EMPTY_WIZARD_DATA: Omit<ProgramWizardData, 'batteryId'> = {
  operatingMode: null,
  maxInputCurrent: null,
  genSetData: {
    1: null,
    2: null,
  },
  loadShareMasterType: null,
  shutDown24vEnabled: null,
};

export const getInitialWizardData = (batteryId: string): ProgramWizardData => ({
  ...EMPTY_WIZARD_DATA,
  batteryId,
});

// Only the shutdown program needs the device to also power down the 24V batteries.
export const getShutDown24vEnabled = (operatingMode: ProgramWizardOperatingMode | null): boolean | null =>
  operatingMode === ProgramWizardOperatingMode.SHUTDOWN ? true : null;

const isMeasuredVoltage = (voltage: unknown): voltage is number =>
  typeof voltage === 'number' && Number.isFinite(voltage);

// A proven connection blocks shutdown. A missing phase or an unclear breaker must not hide a
// voltage that was actually measured, and an invalid voltage must not hide a closed breaker.
export const isBatteryInputStillConnected = (input?: BatteryInputConnection | null): boolean => {
  if (!input) {
    return false;
  }

  const hasMeasuredVoltage = [input.voltageL1L2In, input.voltageL2L3In, input.voltageL3L1In].some(
    (voltage) => isMeasuredVoltage(voltage) && voltage !== 0
  );

  return hasMeasuredVoltage || input.isBreakerClosed === true;
};

export const applyInputSettingsData = ({
  wizardData,
  data,
}: {
  wizardData: ProgramWizardData;
  data: { maxInputCurrent: string; genSetData1: string };
}): ProgramWizardData => ({
  ...wizardData,
  maxInputCurrent: parseFloat(data.maxInputCurrent),
  genSetData: {
    1: parseFloat(data.genSetData1) || null,
    2: null,
  },
});

const STEP_BEFORE_CONFIRM: Partial<Record<ProgramWizardOperatingMode, ProgramWizardStepValue>> = {
  [ProgramWizardOperatingMode.STANDALONE]: ProgramWizardStep.SELECT_PROGRAM,
  [ProgramWizardOperatingMode.STANDBY]: ProgramWizardStep.SELECT_PROGRAM,
  [ProgramWizardOperatingMode.SHUTDOWN]: ProgramWizardStep.SELECT_PROGRAM,
};

export const getStepBeforeConfirm = (operatingMode: ProgramWizardOperatingMode | null): ProgramWizardStepValue =>
  (operatingMode !== null && STEP_BEFORE_CONFIRM[operatingMode]) || ProgramWizardStep.INPUT_SETTINGS;

export const getTriggerButtonText = ({
  label,
  currentProgram,
  t,
}: {
  label: string | undefined;
  currentProgram: ProgramWizardCurrentProgram | null | undefined;
  t: TranslateFn;
}): string => label || (isProgramActive(currentProgram) ? t('changeProgram') : t('startProgram'));

export const getDialogTitle = ({
  step,
  currentProgram,
  t,
}: {
  step: ProgramWizardStepValue;
  currentProgram: ProgramWizardCurrentProgram | null | undefined;
  t: TranslateFn;
}): string => {
  if (step === ProgramWizardStep.STARTING) {
    return t('settingUpBattery');
  }
  return isProgramActive(currentProgram) ? t('program.title') : t('dialogTitle');
};

export const submitBatteryProgram = ({
  wizardData,
  onSubmit,
  onSuccess,
  onError,
}: {
  wizardData: ProgramWizardData;
  onSubmit: (data: ProgramWizardData) => Promise<string | void>;
  onSuccess: (message?: string | void) => void;
  onError: (error: unknown) => void;
}): void => {
  onSubmit(wizardData).then(onSuccess).catch(onError);
};
